import { Delete } from "@mui/icons-material";
import {
  Autocomplete,
  Button,
  Divider,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import { useEffect, useState } from "react";
import { v4 as uuid } from "uuid";
import { MaintenanceMessageAPI } from "../../../api";
import { useAlert } from "../../../lib/alert";
import {
  BannerPriority,
  MaintenanceMessage,
} from "../../../model/MaintenanceMessage";

const bannerPriorities: BannerPriority[] = [
  "lowest",
  "low",
  "mid",
  "medium",
  "high",
  "highest",
];

const RunwayMessageControl = () => {
  const [messages, setMessages] = useState<MaintenanceMessage[]>([]);
  const [newContent, setNewContent] = useState("");
  const [newUptimeDateTime, setNewUptimeDateTime] = useState("");
  const [newColor, setNewColor] = useState("");
  const [newPriority, setNewPriority] = useState<BannerPriority>("highest");

  const alert = useAlert();

  useEffect(() => {
    MaintenanceMessageAPI.getAll().then((response) => {
      setMessages(response);
    });
  }, []);

  const createMessage = async (
    content: string,
    color: string,
    days: number,
    priority: BannerPriority,
  ) => {
    const expiration = new Date(
      Date.now() + days * 24 * 60 * 60 * 1000,
    ).toISOString();
    const message = {
      id: uuid(),
      content,
      color,
      priority,
      expiration,
    } as MaintenanceMessage;
    try {
      await MaintenanceMessageAPI.create(message);
    } catch {
      alert.error("Failed to create message, check auth.");
      return;
    }
    await refreshMessages();
  };

  const deleteMessage = async (id: string) => {
    try {
      await MaintenanceMessageAPI.remove(id);
    } catch {
      alert.error("Failed to delete, check auth.");
    }
    await refreshMessages();
  };

  const refreshMessages = async () => {
    const updatedMessages = await MaintenanceMessageAPI.getAll();
    setMessages(updatedMessages);
  };

  // Sort messages by priority before displaying
  const priorityOrder: BannerPriority[] = [
    "highest",
    "high",
    "medium",
    "mid",
    "low",
    "lowest",
  ];
  const sortedMessages = [...messages].sort((a, b) => {
    const aPriority = a.priority ?? "lowest";
    const bPriority = b.priority ?? "lowest";
    return priorityOrder.indexOf(aPriority) - priorityOrder.indexOf(bPriority);
  });

  return (
    <Box
      sx={{
        p: 2,
        display: "flex",
        gap: 3,
        flexDirection: "column",
      }}
    >
      <Typography variant="h1">Maintenance Message Dashboard</Typography>
      {/* Display all active messages */}
      <Paper
        elevation={2}
        sx={{
          borderRadius: 3,
          p: 2,
          background: (t) => t.palette.grey[100],
        }}
      >
        <Typography sx={{ mb: 1 }} variant="h4">
          {sortedMessages.length > 0 ? "Active Messages" : "No Active Messages"}
        </Typography>
        {sortedMessages.length > 0 && <Divider sx={{ mb: 1 }} />}

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            flexDirection: "column",
          }}
        >
          {sortedMessages.map((message, index) => {
            const expires = new Date(message.expiration ?? "").toLocaleString();
            return (
              <Paper
                key={index}
                elevation={3}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  width: "100%",
                  p: 2,
                  mb: 1,
                  background: (t) => t.palette.grey[50],
                  borderLeft: `6px solid ${message.color}`,
                }}
              >
                <Box
                  sx={(t) => ({
                    width: "16px",
                    height: "16px",
                    background: message.color,
                    border: "2px solid " + t.palette.grey[400],
                    borderRadius: "50%",
                    mr: 2,
                  })}
                />
                <Box sx={{ flexGrow: 1 }}>
                  <Box
                    sx={{
                      alignItems: "center",
                      width: "100%",
                      display: "flex",
                      flexDirection: "row",
                    }}
                  >
                    <Box
                      sx={{
                        px: 0.8,
                        py: 0.2,
                        mr: 1,
                        borderRadius: 2,
                        background: message.color,
                        display: "flex",
                        alignItems: "center",
                        color: (t) => t.palette.getContrastText(message.color),
                      }}
                    >
                      <Typography variant="caption">{`${message.priority?.toUpperCase()} PRIORITY`}</Typography>
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: message.color,
                        flexGrow: 1,
                      }}
                    >
                      {message.content}
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Expires: {expires}
                  </Typography>
                </Box>
                <Box sx={{ ml: 2 }}>
                  <Tooltip title="Delete this message">
                    <IconButton onClick={() => deleteMessage(message.id)}>
                      <Delete />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Paper>
            );
          })}
        </Box>
      </Paper>

      {/* Create message */}
      <Paper
        elevation={2}
        sx={{
          p: 2,
          borderRadius: 3,
          background: (t) => t.palette.grey[100],
        }}
      >
        <Typography sx={{ mb: 1 }} variant="h4">
          {`Create New Message`}
        </Typography>
        <Divider sx={{ mb: 1 }} />
        <Box
          component="form"
          onSubmit={(e) => {
            e.preventDefault();
            if (!newContent || !newUptimeDateTime || !newColor) return;
            // Calculate days (decimal) from now to selected date/time
            const now = new Date();
            const selectedDateTime = new Date(newUptimeDateTime);
            const diffMs = selectedDateTime.getTime() - now.getTime();
            const diffDays = diffMs / (1000 * 60 * 60 * 24);
            createMessage(newContent, newColor, diffDays, newPriority);
            setNewContent("");
            setNewUptimeDateTime("");
            setNewColor("");
          }}
          sx={{ display: "flex", gap: 2, flexDirection: "column" }}
        >
          <TextField
            label="Message"
            fullWidth
            value={newContent}
            onChange={(e) => setNewContent(e.target.value)}
          />
          <TextField
            label="Expiration Date & Time"
            type="datetime-local"
            fullWidth
            value={newUptimeDateTime}
            onChange={(e) => setNewUptimeDateTime(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <Autocomplete
            options={bannerPriorities}
            value={newPriority}
            onChange={(_e, v) => setNewPriority(v ?? "highest")}
            renderInput={(params) => (
              <TextField {...params} label="Priority" fullWidth />
            )}
            sx={{ minWidth: 200 }}
          />
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                border: "2px solid #ccc",
                background: /^#[0-9A-Fa-f]{6}$/.test(newColor)
                  ? newColor
                  : "#ccc",
                transition: "background 0.2s",
                mx: 1,
              }}
            />
            <TextField
              label="Color (hex)"
              value={newColor}
              onChange={(e) => {
                let val = e.target.value;
                // Always start with #
                if (!val.startsWith("#")) {
                  val = "#" + val.replace(/[^0-9A-Fa-f]/g, "");
                }
                // Only allow up to 7 chars and valid hex
                if (/^#[0-9A-Fa-f]{0,6}$/.test(val)) {
                  setNewColor(val);
                }
              }}
              inputProps={{ maxLength: 7 }}
              error={newColor !== "" && !/^#[0-9A-Fa-f]{6}$/.test(newColor)}
              helperText={
                newColor !== "" && !/^#[0-9A-Fa-f]{6}$/.test(newColor)
                  ? "Enter a valid hex color (e.g. #123ABC)"
                  : ""
              }
              sx={{ flex: 1 }}
            />
          </Box>
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button type="submit" variant="contained" color="primary">
              Create
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default RunwayMessageControl;
